import { useDebounce } from "@uidotdev/usehooks";
import { AxiosError } from "axios";
import { useState } from "react";
import { useQuery } from "react-query";
import SearchSelect from "./SearchSelect";
import { FetchPaginatedApiModel } from "../../models/shared/CommonModels";

export interface DeferredSearchSelectProps<T> {
  label: string;
  labelPresentation: (option: T) => string;
  searchQueryKey: string | undefined;
  searchFunction: (
    searchKey: string | undefined
  ) => Promise<FetchPaginatedApiModel<T[]>>;
  onChange: (newInputValue: T | null) => void;
  onError: (error: AxiosError) => void;
}

const DeferredSearchSelect = <T,>(params: DeferredSearchSelectProps<T>) => {
  const [loading, setLoading] = useState(false);

  const [searchedData, setSearchedData] = useState<T[]>([]);
  const [searchDataKey, setSearchDataKey] = useState<string>();
  const deferredSearchData = useDebounce(searchDataKey, 1000);

  const handleClose = () => {
    setSearchedData([]);
    setLoading(false);
  };

  // search data query
  const { refetch, isFetching } = useQuery({
    queryKey: [params.searchQueryKey, deferredSearchData],
    queryFn: () => {
      return params.searchFunction(searchDataKey);
    },
    onSuccess: (response: FetchPaginatedApiModel<T[]>) => {
      if (response.data.length > 0) {
        setSearchedData(response.data);
      } else {
        setSearchedData([]);
      }
      setLoading(false);
    },
    refetchOnWindowFocus: false,
    onError: (error: AxiosError) => {
      params.onError(error);
      setLoading(false);
    },
  });

  return (
    <>
      <SearchSelect
        options={searchedData}
        label={params.label}
        required={false}
        labelPresentation={(option: T) => params.labelPresentation(option)}
        onChange={(event, newInputValue: T | null) => {
          params.onChange(newInputValue);
        }}
        onOpen={() => {
          setLoading(true);
          setSearchedData([]);
          refetch();
        }}
        onClose={handleClose}
        onInputChange={(event, newInputValue: string) => {
          setLoading(true);
          setSearchedData([]);
          setSearchDataKey(newInputValue);
        }}
        loading={loading}
      />
    </>
  );
};

export default DeferredSearchSelect;
